import { useState } from "react";
import { apiGet } from "../lib/api";
import { useAccessToken } from "../lib/useAccessToken";
import { formatAmount, type Network } from "../lib/units";
import CryptoPayButton from "../components/billing/CryptoPayButton";
import StripePayButton from "../components/billing/StripePayButton";
import GraphView from "../components/graph/GraphView";
import ReportPanel from "../components/reports/ReportPanel";
import type { RawGraph } from "../components/graph/types";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { StatTile } from "../components/ui/StatTile";
import { SectionHeader } from "../components/ui/SectionHeader";
import { IconArrowDown, IconArrowUp, IconScale } from "../components/ui/icons";

interface AddressActivity {
  address: string;
  fundedSats: number;
  spentSats: number;
  txCount: number;
}
interface Attribution {
  label: string | null;
  confidence: "confirmed-fact" | "inferred" | "third-party-attribution" | "unknown";
  source: string | null;
}
interface AddressResult {
  activity: AddressActivity;
  apparentBalanceSats: number;
  attribution: Attribution;
}
interface TxIO {
  address?: string;
  valueSats: number;
}
interface NormalizedTx {
  txid: string;
  inputs: TxIO[];
  outputs: TxIO[];
  feeSats: number;
  confirmed: boolean;
  blockTime?: number;
}

const NETWORKS: { id: Network; label: string }[] = [
  { id: "bitcoin", label: "Bitcoin" },
  { id: "ethereum", label: "Ethereum" },
  { id: "solana", label: "Solana" },
  { id: "tron", label: "Tron" },
];

// Ethereum tx hashes are 0x-prefixed 64 hex chars; Bitcoin's and Tron's
// are bare 64 hex chars (indistinguishable from each other by shape
// alone, but the network is already known from the selector). Solana
// signatures are base58, consistently 87-88 chars (a 64-byte ed25519
// signature); Solana addresses (32-byte pubkeys) are shorter, so length
// alone reliably tells them apart. Tron addresses are base58, always 34
// chars starting with "T" — well clear of a 64-char hex hash either way.
function looksLikeTxid(network: Network, q: string): boolean {
  if (network === "ethereum") return /^0x[0-9a-fA-F]{64}$/.test(q);
  if (network === "solana") return /^[1-9A-HJ-NP-Za-km-z]{80,90}$/.test(q);
  return /^[0-9a-fA-F]{64}$/.test(q); // bitcoin and tron
}

const QUERY_LABEL: Record<Network, string> = {
  bitcoin: "Bitcoin address or transaction hash",
  ethereum: "Ethereum address or transaction hash",
  solana: "Solana address or transaction signature",
  tron: "Tron address or transaction hash",
};

const QUERY_PLACEHOLDER: Record<Network, string> = {
  bitcoin: "bc1q… or a 64-character transaction hash",
  ethereum: "0x… address or 0x… transaction hash",
  solana: "A base58 address or transaction signature",
  tron: "T… address or a 64-character transaction hash",
};

export default function Trace() {
  const [network, setNetwork] = useState<Network>("bitcoin");
  const [query, setQuery] = useState("");
  const [addressResult, setAddressResult] = useState<AddressResult | null>(null);
  const [txs, setTxs] = useState<NormalizedTx[] | null>(null);
  const [graph, setGraph] = useState<RawGraph | null>(null);
  const [rootAddress, setRootAddress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const { accessToken, setAccessToken, tokenPending } = useAccessToken();

  const hasResults = Boolean(addressResult || (txs && txs.length > 0));

  function selectNetwork(next: Network) {
    if (next === network) return;
    setNetwork(next);
    setQuery("");
    setAddressResult(null);
    setTxs(null);
    setGraph(null);
    setRootAddress(null);
    setError(null);
  }

  async function search() {
    setError(null);
    setAddressResult(null);
    setTxs(null);
    setGraph(null);
    setRootAddress(null);
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    try {
      if (looksLikeTxid(network, q)) {
        const { transaction } = await apiGet<{ transaction: NormalizedTx }>(`/api/trace/${network}/tx/${q}`);
        setTxs([transaction]);
      } else {
        const [addr, txList, graphData] = await Promise.all([
          apiGet<AddressResult>(`/api/trace/${network}/address/${q}`),
          apiGet<{ transactions: NormalizedTx[] }>(`/api/trace/${network}/address/${q}/transactions`),
          apiGet<RawGraph>(`/api/trace/${network}/graph?address=${q}`),
        ]);
        setAddressResult(addr);
        setTxs(txList.transactions);
        setGraph(graphData);
        setRootAddress(q);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Reports (CSV/PDF) and the paid deep-trace unlock are Bitcoin-only —
  // report-gen and the billing plans behind them were never extended to
  // Ethereum. Don't show either for an Ethereum lookup; that would imply
  // a capability that doesn't exist. Ethereum here is Trace-only, full
  // stop — Recover is unaffected and remains Bitcoin-only regardless.
  const showBillingPanels = network === "bitcoin";

  const unlockPanel = (
    <Card padding="lg">
      <h2 className="font-display text-lg mb-1">Unlock full investigation</h2>
      <p className="text-sm text-muted mb-4">
        Deeper multi-hop tracing and a PDF report. Paid in card or Bitcoin — your choice.
      </p>
      {accessToken ? (
        <p className="text-sm text-signal-400">Unlocked — download the full report above.</p>
      ) : tokenPending ? (
        <p className="text-sm text-muted">Confirming your payment…</p>
      ) : (
        <>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mb-4" />
          <div className="flex flex-wrap gap-3">
            {email ? (
              <>
                <CryptoPayButton
                  planId="trace-report-unlock"
                  customerEmail={email}
                  onConfirmed={setAccessToken}
                />
                <StripePayButton planId="trace-report-unlock" customerEmail={email} />
              </>
            ) : (
              <p className="text-xs text-muted">Enter an email above to see payment options.</p>
            )}
          </div>
        </>
      )}
    </Card>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <h1 className="font-display text-2xl mb-1">Trace where your crypto went</h1>
      <p className="text-sm text-muted mb-8">
        Uses only public blockchain data. Never enter a seed phrase, private key, or password here.
      </p>

      <Card padding="lg">
        <div className="mb-4 flex gap-2" role="group" aria-label="Network">
          {NETWORKS.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => selectNetwork(n.id)}
              className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                network === n.id
                  ? "border-ledger-500 text-paper bg-ledger-500/10"
                  : "border-ink-700 text-muted hover:text-paper"
              }`}
            >
              {n.label}
            </button>
          ))}
        </div>
        <label className="block text-sm text-muted mb-2" htmlFor="query">
          {QUERY_LABEL[network]}
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            id="query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={QUERY_PLACEHOLDER[network]}
            className="font-mono"
          />
          <Button onClick={search} disabled={loading || !query.trim()} className="sm:shrink-0">
            {loading ? "Looking up…" : "Investigate"}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
      </Card>

      {addressResult && (
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <StatTile
            icon={<IconArrowDown className="w-4 h-4" />}
            label={`Received · ${addressResult.activity.txCount} tx${addressResult.activity.txCount === 1 ? "" : "s"}`}
            value={formatAmount(addressResult.activity.fundedSats, network)}
          />
          <StatTile
            icon={<IconArrowUp className="w-4 h-4" />}
            label="Sent"
            value={formatAmount(addressResult.activity.spentSats, network)}
          />
          <StatTile
            icon={<IconScale className="w-4 h-4" />}
            label="Apparent balance"
            value={formatAmount(addressResult.apparentBalanceSats, network)}
          />
        </div>
      )}

      {network === "ethereum" && addressResult && (
        <p className="mt-2 text-xs text-muted">
          Ethereum figures are the current on-chain balance and recent native ETH transfers only — not a
          lifetime received/sent total like Bitcoin's, and excluding ERC-20 token transfers and internal
          (contract-triggered) transactions for now.
        </p>
      )}

      {network === "solana" && addressResult && (
        <p className="mt-2 text-xs text-muted">
          Solana figures are the current on-chain balance and recent native SOL transfers only — not a
          lifetime received/sent total like Bitcoin's, and excluding SPL token transfers for now.
        </p>
      )}

      {network === "tron" && addressResult && (
        <p className="mt-2 text-xs text-muted">
          Tron figures are the current on-chain balance and recent native TRX transfers only — not a
          lifetime received/sent total like Bitcoin's, and excluding TRC-20 tokens (including USDT, most
          of this network's real traffic) for now.
        </p>
      )}

      {addressResult && (
        <Card padding="md" className="mt-4">
          <SectionHeader title="Attribution" />
          {addressResult.attribution.confidence === "unknown" ? (
            <p className="text-sm text-muted">No attribution available for this address — ownership is unknown.</p>
          ) : (
            <p className="text-sm">
              Associated with {addressResult.attribution.label}, according to{" "}
              {addressResult.attribution.source} ({addressResult.attribution.confidence}). This is a
              third-party attribution, not a confirmed identity.
            </p>
          )}
        </Card>
      )}

      {graph && rootAddress && (
        <Card padding="md" className="mt-6">
          <SectionHeader title="Fund flow graph" />
          <GraphView initialGraph={graph} rootAddress={rootAddress} network={network} />
        </Card>
      )}

      {hasResults && (
        <div className="mt-6 grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2">
            {txs && txs.length > 0 && (
              <Card padding="md">
                <SectionHeader title={`Transactions${txs.length > 1 ? " (most recent first)" : ""}`} />
                <ul className="space-y-3">
                  {txs.slice(0, 10).map((tx) => (
                    <li
                      key={tx.txid}
                      className="border border-ink-700 rounded-md p-3 text-xs font-mono bg-ink-950/40"
                    >
                      <p className="break-all text-ledger-400">{tx.txid}</p>
                      <p className="mt-1 text-muted font-body">
                        {tx.confirmed ? "Confirmed" : "Unconfirmed"}
                        {tx.blockTime ? ` · ${new Date(tx.blockTime * 1000).toLocaleString()}` : ""} · fee{" "}
                        {formatAmount(tx.feeSats, network)}
                      </p>
                      <p className="mt-2">
                        {tx.inputs.length} input{tx.inputs.length === 1 ? "" : "s"} →{" "}
                        {tx.outputs.length} output{tx.outputs.length === 1 ? "" : "s"}
                      </p>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {showBillingPanels ? (
            <div className="flex flex-col gap-6">
              {rootAddress && <ReportPanel address={rootAddress} accessToken={accessToken} />}
              {unlockPanel}
            </div>
          ) : (
            <Card padding="lg">
              <p className="text-sm text-muted">
                Reports and the paid deep-trace unlock are Bitcoin-only for now. Ethereum support here is
                free lookup and graph tracing only.
              </p>
            </Card>
          )}
        </div>
      )}

      {!hasResults && showBillingPanels && <div className="mt-10">{unlockPanel}</div>}
    </div>
  );
}
